<script setup lang="ts">
import type { GiratudoHistoricoEntry } from '@/types/giratudo';
import GiratudoPagination from '@/components/apostatudo/atoms/GiratudoPagination.vue';
import { getAssetsUrlForCDN } from '@/utils/template';

interface Props {
  title?: string;
  data: GiratudoHistoricoEntry[];
  page: number;
  totalPages?: number;
  loading?: boolean;
}

const {
  title,
  data,
  page,
  totalPages = 1,
  loading = false,
} = defineProps<Props>();

const emit = defineEmits<{
  'update:page': [page: number];
}>();

function getRowBg(position: number): string {
  if (position === 1)
    return 'linear-gradient(92deg, #f00 -12.63%, #ff4f15 33.35%, rgba(255, 79, 21, 0.5) 66.54%)';
  if (position === 2)
    return 'linear-gradient(92deg, #1414f0 -12.63%, #1414f0 33.35%, rgba(20, 20, 240, 0.5) 66.54%)';
  if (position === 3)
    return 'linear-gradient(92deg, rgba(114, 114, 248, 0.9) 8.22%, rgba(114, 114, 248, 0.9) 50.38%, rgba(114, 114, 248, 0.45) 84.34%)';

  return 'rgba(1, 67, 227, 0.10)';
}

function getPosBadgeUrl(position: number): string {
  if (position <= 3)
    return getAssetsUrlForCDN(`/assets/images/giratudo/${position}.png`);

  return getAssetsUrlForCDN('/assets/images/giratudo/4.png');
}

// ── CUSTOMIZAR AQUI: larguras das colunas ──
const { isMobile } = useLayout();

const gridCols = computed(() => {
  if (isMobile.value) {
    return '48px 1fr 52px 64px 58px';
  }

  return 'minmax(40px,64px) minmax(0,1fr) minmax(30px,55px) minmax(50px,90px) minmax(45px,75px)';
});

// ── CUSTOMIZAR AQUI: paddings ──
const rowPadding = 'mx-2';
const headerPadding = 'px-2 pt-1.5 pb-1';
</script>

<template>
  <div
    class="flex flex-col max-h-full bg-bg-primary overflow-hidden border border-[#1e3a8a]/40"
    :class="[title ? 'rounded-lg' : 'rounded-b-lg']"
  >
    <!-- Title -->
    <div
      v-if="title"
      class="shrink-0 px-3 py-3 border-b border-[#06b6d4]/30"
      style="
        background: linear-gradient(180deg, #0143e3 0%, #000717 100%);
        border-radius: 10px 10px 0 0;
      "
    >
      <h3 class="text-center text-sm font-bold tracking-widest !text-[#ffffff]">
        {{ title }}
      </h3>
    </div>

    <!-- Column Headers -->
    <div
      class="shrink-0 grid items-center border-b border-[#1e3a8a]/50 text-[10px] font-semibold uppercase tracking-widest !text-[#ffffff]"
      :class="headerPadding"
      :style="{ gridTemplateColumns: gridCols }"
    >
      <span class="text-center">POS</span>
      <span class="text-center">DATA</span>
      <span class="text-center">PTS</span>
      <span class="text-center">PRÊMIO</span>
      <span class="text-center">CRIADO</span>
    </div>

    <!-- Table Body -->
    <div
      class="flex-1 min-h-0 overflow-y-auto historico-scrollbar py-1.5 flex flex-col gap-1"
    >
      <!-- Loading -->
      <template v-if="loading">
        <div
          v-for="i in isMobile ? 3 : 10"
          :key="`loading-${i}`"
          :class="rowPadding"
          :style="{
            height: '36px',
            minHeight: '36px',
            maxHeight: '36px',
            background: 'rgba(1, 67, 227, 0.04)',
            borderRadius: '9.827px',
          }"
        />
      </template>

      <!-- Empty -->
      <template v-else-if="data.length === 0">
        <template v-if="!isMobile">
          <div
            v-for="i in 10"
            :key="`empty-${i}`"
            :class="rowPadding"
            :style="{
              height: '36px',
              minHeight: '36px',
              maxHeight: '36px',
              background: 'rgba(1, 67, 227, 0.04)',
              borderRadius: '9.827px',
            }"
          />
        </template>
      </template>

      <!-- Rows -->
      <template v-else>
        <div
          v-for="(row, idx) in data"
          :key="idx"
          class="grid items-center text-[11px] text-[#ffffff]/90 transition-colors duration-150"
          :class="rowPadding"
          :style="`grid-template-columns: ${gridCols}; height: 36px; min-height: 36px; max-height: 36px; border-radius: 9.827px; background: ${getRowBg(row.position ?? 0)} !important;`"
        >
          <!-- POS -->
          <span class="flex items-center justify-center">
            <span
              v-if="row.position && row.position <= 10"
              class="inline-flex h-7 w-7 items-center justify-center bg-contain bg-center bg-no-repeat pos-badge"
              :class="[row.position <= 3 ? 'pos-badge-top' : 'pb-1']"
              :style="{
                backgroundImage: `url(${getPosBadgeUrl(row.position)})`,
              }"
            >
              {{ row.position }}
            </span>
            <span
              v-else-if="row.position"
              class="pos-badge pos-badge-plain"
            >
              {{ row.position }}
            </span>
            <span
              v-else
              class="text-[#ffffff]/40 text-[10px] font-semibold"
            >
              —
            </span>
          </span>

          <!-- DATA -->
          <span
            class="text-center text-[9px] leading-tight font-extrabold text-[#ffffff]/70"
          >
            {{ row.date?.split(' ')[0] }}<br />{{ row.date?.split(' ')[1] }}
          </span>

          <!-- PTS -->
          <span class="truncate text-center font-semibold !text-[#ffffff]">
            {{ row.ticketNumber }}
          </span>

          <!-- PREMIO -->
          <span class="truncate text-center text-[#4ade80]/80">
            {{ row.prize }}
          </span>

          <!-- CRIADO -->
          <span class="truncate text-center text-[#ffffff]/60">
            {{ row.createdAt }}
          </span>
        </div>

        <!-- Filler rows (desktop only) -->
        <template v-if="!isMobile">
          <div
            v-for="i in Math.max(0, 10 - data.length)"
            :key="`filler-${i}`"
            :class="rowPadding"
            :style="{
              height: '36px',
              minHeight: '36px',
              maxHeight: '36px',
              background: 'rgba(1, 67, 227, 0.04)',
              borderRadius: '9.827px',
            }"
          />
        </template>
      </template>
    </div>

    <!-- Pagination -->
    <GiratudoPagination
      :always-show="true"
      class="shrink-0"
      :page="page"
      :total-pages="totalPages"
      @update:page="emit('update:page', $event)"
    />
  </div>
</template>

<style scoped>
.pos-badge {
  color: #f5feff;
  text-align: center;
  text-shadow: 0.295px 0.295px 0.393px #00054c;
  -webkit-text-stroke-width: 0.2px;
  -webkit-text-stroke-color: #1d23df;
  font-size: 10.81px;
  font-style: normal;
  font-weight: 800;
  line-height: 100%;
}

.pos-badge-top {
  -webkit-text-stroke-width: 0.6px;
  font-size: 13px;
  padding-top: 6px;
  box-sizing: border-box;
}

.pos-badge-plain {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(255, 255, 255, 0.05);
  font-size: 10.81px;
  -webkit-text-stroke-width: 0;
}

.placar-row-gold {
  border-radius: 10.843px;
  background: linear-gradient(
    92deg,
    #f00 -12.63%,
    #ff4f15 33.35%,
    rgba(255, 79, 21, 0.5) 66.54%
  );
}

.placar-row-silver {
  border-radius: 10.843px;
  background: linear-gradient(
    92deg,
    #1414f0 -12.63%,
    #1414f0 33.35%,
    rgba(20, 20, 240, 0.5) 66.54%
  );
}

.placar-row-bronze {
  border-radius: 10.843px;
  background: linear-gradient(
    92deg,
    rgba(114, 114, 248, 0.9) 8.22%,
    rgba(114, 114, 248, 0.9) 50.38%,
    rgba(114, 114, 248, 0.45) 84.34%
  );
}

.historico-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.historico-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.historico-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(59, 130, 246, 0.3);
  border-radius: 2px;
}

.historico-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(59, 130, 246, 0.5);
}
</style>
